import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { AvatarConfig } from "@shared/types";
import { SoftBox } from "../game/engine/roundedBoxes";
import { buildRunnerCharacter } from "../game/engine/runnerCharacter";
import { isLowQuality } from "../game/engine/quality";

export interface BattlePodiumPlayer {
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
}

interface Props {
  players: BattlePodiumPlayer[];
  /** Final-level (Battle Royale) celebration: confetti rains DOWN and the
   *  winners drop out of the sky onto the podium (staggered, winner last)
   *  with an impact squash. Off → the standard instant-stand podium. */
  finale?: boolean;
}

export function BattlePodiumScene({ players, finale = false }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  // Rebuild the scene only when the podium's *content* changes — not on every
  // new `players` array reference. The parent (RaceResults) re-renders ~2×/sec
  // while the level-vote timer ticks, handing us a fresh array each time; keying
  // the effect on a stable signature stops the 3D scene (and its dance loop)
  // from being torn down + restarted every render.
  const playersKey = players
    .map((p) => `${p.id}:${p.displayName}:${JSON.stringify(p.avatarConfig)}`)
    .join("|");

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const low = isLowQuality();
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: !low, alpha: true });
    renderer.setPixelRatio(low ? 1 : Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = !low;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    // Wide-FOV, closer camera so the celebrating runners read big.
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 90);
    camera.position.set(2.4, 3.6, 5.2);
    camera.lookAt(0, 2.4, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 1.15));
    const sun = new THREE.DirectionalLight(0xffffff, 1.8);
    sun.position.set(5, 8, 6);
    sun.castShadow = true;
    scene.add(sun);
    const rim = new THREE.PointLight(0xff2ca4, 2.8, 18);
    rim.position.set(-4.5, 3.2, 4.5);
    scene.add(rim);

    const floor = new THREE.Mesh(
      new THREE.CylinderGeometry(4.6, 5.1, 0.36, 48),
      new THREE.MeshStandardMaterial({ color: 0x25c6f7, roughness: 0.38, metalness: 0.08 }),
    );
    floor.position.y = 0.18;
    floor.receiveShadow = true;
    scene.add(floor);

    const slots = [
      { place: 1, x: 0, h: 1.15, color: 0xffd93d },
      { place: 2, x: -2.05, h: 0.86, color: 0xb8c4d8 },
      { place: 3, x: 2.05, h: 0.68, color: 0xff8b3d },
    ];
    const dancers: Dancer[] = [];
    // Drop-in tuning (finale only). Winner (place 1) lands LAST + highest so
    // the eye finishes on them. Heights/delays in metres / seconds.
    const DROP_HEIGHT = 7.5;
    const DROP_DUR = 0.5;
    const dropDelayFor = (place: number) => (place === 3 ? 0.45 : place === 2 ? 0.8 : 1.2);

    for (const slot of slots) {
      const step = new THREE.Mesh(
        new SoftBox(1.65, slot.h, 1.55),
        new THREE.MeshStandardMaterial({ color: slot.color, roughness: 0.42, metalness: 0.12 }),
      );
      step.position.set(slot.x, 0.36 + slot.h / 2, 0);
      step.castShadow = true;
      step.receiveShadow = true;
      scene.add(step);

      const label = makeTextSprite(`${slot.place}`);
      label.position.set(slot.x, 0.55 + slot.h, 0.82);
      label.scale.set(0.62, 0.62, 1);
      scene.add(label);

      const player = players[slot.place - 1];
      if (!player) continue;
      const dancer = buildDancer(player.avatarConfig);
      const restY = slot.h + 0.36;
      dancer.restY = restY;
      dancer.dropDelay = finale ? dropDelayFor(slot.place) : -1;
      dancer.root.rotation.y = slot.place === 1 ? 0 : slot.place === 2 ? 0.18 : -0.18;
      if (finale) {
        // Park them in the sky, hidden, until their drop begins.
        dancer.root.position.set(slot.x, restY + DROP_HEIGHT, -0.05);
        dancer.root.visible = false;
      } else {
        dancer.root.position.set(slot.x, restY, -0.05);
      }
      scene.add(dancer.root);
      dancers.push(dancer);

      // Name floats above the character's head (billboarded sprite, so it
      // stays readable as the camera orbits).
      const name = makeTextSprite(player.displayName.slice(0, 12), 0x10204b, 0xffffff);
      name.position.set(slot.x, slot.h + 2.7, 0);
      name.scale.set(1.7, 0.5, 1);
      scene.add(name);
    }

    const confetti: ConfettiPiece[] = [];
    const colors = [0xff2ca4, 0xffd93d, 0x28d7ff, 0x5cff74, 0xff8b3d, 0xffffff];
    const CONFETTI_COUNT = low ? (finale ? 28 : 14) : (finale ? 90 : 42);
    const CONFETTI_TOP = 7.5;
    for (let i = 0; i < CONFETTI_COUNT; i++) {
      const piece = new THREE.Mesh(
        new SoftBox(0.08, 0.045, 0.13),
        new THREE.MeshBasicMaterial({ color: colors[i % colors.length] }),
      ) as unknown as ConfettiPiece;
      // Finale: pre-fill the whole column so confetti is already raining from
      // the first frame. Otherwise the original mid-height scatter.
      const y = finale ? 0.5 + Math.random() * CONFETTI_TOP : 1.4 + Math.random() * 4;
      piece.position.set((Math.random() - 0.5) * 8, y, -1.4 + (Math.random() - 0.5) * 3.8);
      piece.fallSpeed = 0.03 + Math.random() * 0.045;     // m per 30fps frame
      piece.flutterAmp = 0.006 + Math.random() * 0.01;
      piece.flutterPhase = Math.random() * Math.PI * 2;
      scene.add(piece);
      confetti.push(piece);
    }

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let last = 0;
    const frameMs = 1000 / 30;
    const start = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < frameMs) return;
      last = now;
      const t = (now - start) * 0.001;

      dancers.forEach((dancer, i) => {
        // Finale drop-in: park in the sky → fall (ease-in) → land with an
        // impact squash → switch to the looping celebration.
        if (finale && dancer.dropDelay >= 0) {
          const p = (t - dancer.dropDelay) / DROP_DUR;
          if (p <= 0) {
            dancer.root.visible = false;
            animateDancer(dancer, 0); // neutral pose while waiting
            return;
          }
          dancer.root.visible = true;
          if (p < 1) {
            const fall = 1 - p * p; // 1 (high) → 0 (rest), accelerating
            dancer.root.position.y = dancer.restY + DROP_HEIGHT * fall;
            dancer.root.scale.setScalar(1);
            animateDancer(dancer, 0); // tucked/neutral during the fall
            return;
          }
          dancer.root.position.y = dancer.restY;
          const sinceLand = t - (dancer.dropDelay + DROP_DUR);
          const squash = sinceLand < 0.3 ? Math.sin((sinceLand / 0.3) * Math.PI) : 0;
          dancer.root.scale.set(1 + squash * 0.28, 1 - squash * 0.34, 1 + squash * 0.28);
          animateDancer(dancer, sinceLand + i * 0.4);
          return;
        }
        animateDancer(dancer, t + i * 0.62);
      });
      confetti.forEach((piece) => {
        if (finale) {
          // Rain DOWN with a side-to-side flutter; recycle to the top.
          piece.position.y -= piece.fallSpeed;
          piece.position.x += Math.sin(t * 2 + piece.flutterPhase) * piece.flutterAmp;
          piece.rotation.x += 0.08;
          piece.rotation.z += 0.06;
          if (piece.position.y < 0.2) {
            piece.position.y = CONFETTI_TOP + Math.random() * 0.8;
            piece.position.x = (Math.random() - 0.5) * 8;
          }
        } else {
          piece.position.y += 0.012;
          piece.rotation.x += 0.07;
          piece.rotation.y += 0.05;
          if (piece.position.y > 5.7) piece.position.y = 1.0;
        }
      });

      // Slow orbit around the podium so we see all three winners in turn.
      const orbit = t * 0.22;
      camera.position.x = Math.sin(orbit) * 2.6;
      camera.position.z = 5.0 + Math.cos(orbit) * 0.6;
      camera.position.y = 3.4 + Math.sin(t * 0.9) * 0.15;
      camera.lookAt(0, 2.4, 0);
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        } else if (o instanceof THREE.Sprite) {
          o.material.map?.dispose();
          o.material.dispose();
        }
      });
    };
    // playersKey is the stable content signature; `players` is read inside but
    // intentionally not a dep (its identity churns every parent render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playersKey, finale]);

  return <canvas ref={ref} className="battle-podium-scene" />;
}

interface Dancer {
  root: THREE.Group;
  body: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  /** Resting Y of the root once standing on the podium step. */
  restY: number;
  /** Finale drop-in start time (s). <0 → no drop (stands immediately). */
  dropDelay: number;
}

/** A confetti mesh with per-piece fall/flutter params (finale rain). */
type ConfettiPiece = THREE.Mesh & {
  fallSpeed: number;
  flutterAmp: number;
  flutterPhase: number;
};

function buildDancer(avatar: AvatarConfig): Dancer {
  // Shared runner geometry — matches the in-game player and every other
  // preview screen. Wrap it in a `root` so we can size the podium-step
  // dancer at 0.5× without disturbing the rest of the scene.
  const parts = buildRunnerCharacter(avatar);
  parts.body.scale.setScalar(0.5);
  // Uniformly shrink the whole dancer (body + legs) — they were reading too
  // big on the podium. Applied on the inner group so it composes cleanly with
  // the per-frame body bounce (SCALE) and the finale drop-in squash (root).
  parts.group.scale.setScalar(0.55);
  const root = new THREE.Group();
  root.add(parts.group);
  return { root, body: parts.body, leftLeg: parts.leftLeg, rightLeg: parts.rightLeg, restY: 0, dropDelay: -1 };
}

function animateDancer(dancer: Dancer, t: number) {
  // Larger, looping celebration. Body scaled WAY up so the runners read
  // big on screen and the bounce is more theatrical. The sin-based
  // formulas already loop forever — no clamps, no end conditions.
  const SCALE = 1.6;
  const bounce = Math.abs(Math.sin(t * 3.4)) * 0.45;
  const sway = Math.sin(t * 3.4);
  const breath = Math.sin(t * 2.2) * 0.05;
  // Cheer-jump every ~2.2s — an extra hop on top of the running bounce.
  const cheerPhase = (t * 0.45) % 1;
  const cheerHop = cheerPhase < 0.35 ? Math.sin(cheerPhase / 0.35 * Math.PI) * 0.6 : 0;
  dancer.body.position.y = bounce + cheerHop;
  dancer.body.rotation.y = Math.sin(t * 2.1) * 0.45;
  dancer.body.rotation.z = sway * 0.22;
  dancer.body.rotation.x = Math.abs(sway) * 0.06 - cheerHop * 0.15;
  dancer.body.scale.setScalar(SCALE);
  dancer.body.scale.y = SCALE * (1 + breath);
  dancer.body.scale.x = SCALE * (1 - breath * 0.2);
  dancer.body.scale.z = SCALE * (1 - breath * 0.2);
  dancer.leftLeg.rotation.x = -sway * 0.55 - cheerHop * 0.6;
  dancer.rightLeg.rotation.x = sway * 0.55 - cheerHop * 0.6;
  dancer.leftLeg.position.y = 0.58 + Math.max(0, sway) * 0.18;
  dancer.rightLeg.position.y = 0.58 + Math.max(0, -sway) * 0.18;
}

// (addHat removed — hats are baked into buildRunnerCharacter now.)

function makeTextSprite(text: string, bg = 0xffffff, fg = 0x111827) {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 96;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = `#${bg.toString(16).padStart(6, "0")}`;
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 8;
  roundedRect(ctx, 12, 18, 232, 60, 18);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = `#${fg.toString(16).padStart(6, "0")}`;
  ctx.font = "900 40px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 128, 49);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true }));
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
